import { Button, FormHelperText, Input, Stack, Tag, TagCloseButton, TagLabel, Text } from "@chakra-ui/react"
import MultiSelectFilter from "components/filter/MultiSelectFilter"
import { useMemo, useState } from "react"
import { normalizeResolutions, parseResolution, resolutionCounts } from "lib/resolution"

// Choice of the resolutions a regular broadcast is made for: any number of them, taken from the
// resolutions the uploaded videos really have, or typed ("1920x1080").
// - value:    the chosen resolutions, ["1280x720", ...] (empty = not limited)
// - onChange: called with the new list
// - content:  all content items, for the list of known resolutions and how many videos have each
export default function ResolutionPicker({ value, onChange, content }) {
  const chosen = useMemo(() => normalizeResolutions(value), [value])
  const [typed, setTyped] = useState("")
  const [invalid, setInvalid] = useState(false)

  // Known resolutions with their counts, plus the chosen ones that no content has (yet)
  const options = useMemo(() => {
    const counts = resolutionCounts(content)
    const known = new Set(counts.map(item => item.key))
    return [
      ...counts.map(item => ({ value: item.key, label: `${item.key} (${item.count})` })),
      ...chosen.filter(key => !known.has(key)).map(key => ({ value: key, label: `${key} (none yet)` })),
    ]
  }, [content, chosen])

  const addTyped = () => {
    const key = parseResolution(typed)
    if (!key) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    setTyped("")
    onChange(normalizeResolutions([...chosen, key]))
  }

  return (
    <Stack direction='column' spacing={2}>
      <Stack direction={{ base: 'column', md: 'row' }} spacing={2} align={{ base: 'stretch', md: 'center' }}>
        <MultiSelectFilter inDrawer label='Choose resolutions' width='240px' options={options} value={chosen} onChange={next => onChange(normalizeResolutions(next))} />
        <Stack direction='row' spacing={2} align='center'>
          <Input
            size='sm'
            w='150px'
            borderRadius='30px'
            placeholder='e.g. 1920x1080'
            value={typed}
            isInvalid={invalid}
            onChange={e => { setTyped(e.target.value); setInvalid(false) }}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTyped() } }}
          />
          <Button size='sm' variant='outline' colorScheme='brand' borderRadius='30px' onClick={addTyped} isDisabled={!typed.trim()}>Add</Button>
        </Stack>
      </Stack>
      {chosen.length > 0 && (
        <Stack direction='row' wrap='wrap' spacing={0} gap={2}>
          {chosen.map(key => (
            <Tag key={key} size='md' variant='subtle' colorScheme='brand' borderRadius='full'>
              <TagLabel>{key}</TagLabel>
              <TagCloseButton aria-label={`Remove ${key}`} onClick={() => onChange(chosen.filter(item => item !== key))} />
            </Tag>
          ))}
        </Stack>
      )}
      {invalid && <Text fontSize='xs' color='red.500'>Write the resolution as width x height, for example 1280x720.</Text>}
      <FormHelperText mt={0}>
        {chosen.length === 0
          ? 'Not limited: all content is offered. Choose resolutions to offer only matching videos and pictures.'
          : 'Only videos and pictures with these resolutions are offered; tick "Show all content" to see the rest.'}
      </FormHelperText>
    </Stack>
  )
}
